import {ChangeDetectionStrategy, Component, ElementRef, OnInit, viewChild} from '@angular/core';
import Modal from "bootstrap/js/dist/modal";
import {NewVisitSummary} from "./visit-overview-item/visit-overview-item.component";
import {sendQuery} from "../../common/utils";
import {
  FacetValueResult,
  FindVisits,
  GetVesselImageUrls,
  SortDirection,
  VisitSummary,
  VisitSummarySortingProperty
} from "@portbase/bezoekschip-service-typescriptmodels";
import {AppContext} from "../../app-context";
import lodash from "lodash";
import {VisitOverviewUtils} from "./visit-overview-item/visit-overview.utils";
import {DateFieldRange, MomentDateFieldRange} from "../../common/date/date-range/date-field-range";
import moment from "moment/moment";
import {ClearanceService} from "../../visit-details/visit/clearance/clearance-service";
import {FacetStats, QuickView} from "../common/facets/facet-filter/facet-filter.component";
import {SentenceCasePipe} from "../../common/sentence-case.pipe";
import {PortvisitUtils} from "../../refdata/portvisit-utils";
import {DataQuery, FacetedOverview} from "../common/overview/faceted-overview";
import {FacetedOverviewFilters} from "../common/overview/overview.component";
import {Handler} from "../../common/handlers/handler";
import {HandleQuery} from "../../common/handlers/handle-query";
import {Observable, of} from "rxjs";
import {now} from "moment";
import {EmergencyProcedureService} from "../../admin/emergencyprocedure/emergencyprocedure.service";

const quickViews: QuickView[] = [{
  code: "ALL_VISITS",
  name: "All visits",
  icon: "fa-calendar-days",
  facets: {}
}, {
  code: "PLANNED_EXPECTED",
  name: "Planned & expected",
  icon: "fa-calendar-day",
  facets: {
    "visitStatus": ["PLANNED", "EXPECTED"]
  }
}, {
  code: "ARRIVED",
  name: "Arrived",
  icon: "fa-calendar-check",
  facets: {
    "visitStatus": ["ARRIVED"]
  }
}, {
  code: "DEPARTED",
  name: "Departed",
  icon: "fa-kit fa-xl fa-pb-calender-arrow-right",
  facets: {
    "visitStatus": ["DEPARTED"]
  }
}, {
  code: "ATTENTION_REQUIRED",
  name: "Attention required",
  icon: "fa-circle-exclamation",
  facets: {},
  queryFn: (query: DataQuery<FindVisits>): DataQuery<FindVisits> => {
    return ({
      ...query,
      payload: {
        ...query.payload,
        attentionRequired: true
      }
    });
  }
}];

@Component({
    selector: 'app-visit-overview',
    templateUrl: './visit-overview.component.html',
    styleUrls: ['./visit-overview.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
@Handler()
export class VisitOverviewComponent extends FacetedOverview<NewVisitSummary, VisitOverviewFilters> implements OnInit {
  appContext = AppContext;
  sortingProperties: VisitSummarySorting[] = [{
    property: VisitSummarySortingProperty.CREATED,
    direction: SortDirection.DESC
  }, {
    property: VisitSummarySortingProperty.ARRIVAL_DATE,
    direction: SortDirection.DESC
  }, {
    property: VisitSummarySortingProperty.ARRIVAL_DATE,
    direction: SortDirection.ASC
  }];
  cachedVisits: { [key: string]: VisitSummary[] } = {};

  vesselImageModal = viewChild<ElementRef>('vesselImageModal');
  private vesselImageModalInstance: Modal;
  zoomedVesselImageUrl: string;
  hiddenFacets: string[] = ["count", "ownerShortName", "declarantShortName", "allowedViewers", "actionRequiredForAgents",
    "actionRequiredForStevedores", "actionRequiredForShipOperator", "activeDeadlinesForStevedores", "hasActiveDeadline",
    "harbourMasterStatus" // At 2026-08-25 this facet was removed from VisitSummary, this facet can, after some time be removed from the hidden list
  ]
    .concat(AppContext.showPortCallOptimization() ? [] : ["rtaUnequalEta", "unequalEtc", "unequalMooring", "unequalBollards"])
    .map(h => h.toLowerCase());
  facetOrdering: string[] = ["visitStatus", "portName", "stevedore", "quay", "tidalShip",
    "unreadHarbourRemarks", "overlandedContainers", "shortlandedContainers"];
  otherFacetNames: string[] = ["overlandedContainers", "shortlandedContainers"];
  totalCount: number = 0;

  constructor(private clearanceService: ClearanceService,
              private emergencyProcedureService: EmergencyProcedureService) {
    super("visit-overview-filters", quickViews, quickViews.find(q => q.code === "ATTENTION_REQUIRED"));
  }

  getDataQuery = (term: string): DataQuery<FindVisits> => {
    const timeRange = this.prepareDateTimeRange(this.filters.overviewFilters.filters.timeRange, !term, {
      start: "2023-01-01T00:00:00.000Z",
      end: moment(now()).endOf("date").add(1, "quarter").toISOString()
    });
    const queryFn = this.filters.overviewFilters.selectedQuickView?.queryFn;
    const query = <DataQuery<FindVisits>>{
      className: "com.portbase.bezoekschip.common.api.visit.FindVisits",
      payload: {
        term: term,
        dateTimeRange: timeRange,
        pagination: { from: this.from, count: this.maxItems},
        sortingProperty: this.sortingProperty.property,
        sortDirection: this.sortingProperty.direction,
        ownCallsOnly: this.filters.ownCallsOnly,
        facetFilters: this.getFacetFilters()
      }
    };

    return queryFn ? queryFn(query) : query;
  }

  ngOnInit(): void {
    if (this.feedbackEnabled()) {
      this.showFeedbackButtonBasedOnLanguage();
    }
  }

  get sortingProperty(): VisitSummarySorting {
    return this.filters?.sorting || (this.filters?.sortingProperty ? {
      property: this.filters.sortingProperty,
      direction: this.filters.sortDirection
    } : null) || {
      property: VisitSummarySortingProperty.ARRIVAL_DATE,
      direction: SortDirection.DESC
    };
  }

  getHiddenFacets = (): string[] => [];

  changeSorting = (sortingProperty: VisitSummarySorting) => {
    this.filters.sorting = sortingProperty;
    this.filters.sortDirection = this.filters.sortingProperty = null;
    this.saveFiltersInLocalStorage(this.filters);
    this.loadData();
  }

  setOwnsCallsOnly = (ownCallsOnly: boolean) => {
    this.filters.ownCallsOnly = ownCallsOnly;
    this.saveFiltersInLocalStorage(this.filters);
    this.loadData();
  }

  arrivalDateChanged = (date: DateFieldRange) => {
    this.filters.overviewFilters.filters.timeRange = date ? <DateFieldRange>{
      start: moment(date.start).toISOString(),
      end: moment(date.end).toISOString()
    } : null;
    this.data = [];
    this.loadData();
  }

  private updateVesselImages(result: VisitSummary[]) {
    return sendQuery("com.portbase.bezoekschip.common.api.visit.GetVesselImageUrls", <GetVesselImageUrls>{
      imoCodes: result.map(visit => visit.imoCode)
    }, {showSpinner: false, caching: false}).subscribe(vesselImages => {
        this.data = this.data.map(visit => {
          if (vesselImages.hasOwnProperty(visit.imoCode)) {
            return {
              ...visit,
              vesselImageUrl: vesselImages[visit.imoCode]
            }
          }
          return visit;
        });
      }
    );
  }

  renderRecords = (result: VisitSummary[], append: boolean) => {
    this.saveFiltersInLocalStorage(this.filters);
    if (!append) {
      this.cachedVisits = {};
    }
    const visits = result.map(v => VisitOverviewUtils.toExtendedSummary(v, this.clearanceService));
    Object.entries(lodash.groupBy(visits, (v) => v.visitStatus))
      .forEach(e => this.cachedVisits[e[0]] = (this.cachedVisits[e[0]] || []).concat(e[1]))
    if (visits.length < this.maxItems) {
      this.endReached = true;
    }
    this.data = append ? (this.data || []).concat(visits) : visits;
    this.updateVesselImages(result)
  }

  protected setFacetsFromResult(facets: { [p: string]: FacetValueResult[] }) {
    super.setFacetsFromResult(facets);
    const countFacet = this.allFacets?.find(e => e?.name.toLowerCase() === "count");
    this.totalCount = countFacet ? lodash.sum(countFacet.values.map(e => e.count)) : 0;
  }

  trackByRecord = (index: number, obj: NewVisitSummary): any => obj.crn;

  openVesselImageModal = (summary: NewVisitSummary) => {
    this.zoomedVesselImageUrl = summary.vesselImageUrl;
    const vesselImageModal = this.vesselImageModal();
    if (!vesselImageModal) {
      return;
    }
    this.vesselImageModalInstance = new Modal(vesselImageModal.nativeElement);
    this.vesselImageModalInstance.show();
  }

  nameFormatter = (name: string): string => {
    switch (name) {
      case "unreadHarbourRemarks": return "Harbour master remarks";
      case "rtaUnequalEta": return "ETA-RTA mismatch";
      case "bunkeringLate": return "Bunkering may/will cause delay";
      case "unequalMooring": return "Mooring mismatch";
      case "unequalBollards": return "Bollards mismatch";
      case "unequalEtc": return "ETD-ETC mismatch";
      case "willBeTransferred": return "Handover to other agent";
      case "hasHarbourDues": return "Harbour dues";
      case "harbourDues/status": return "Harbour dues status";
      default: return SentenceCasePipe.formatWithSpaces(name);
    }
  }

  getSortDirectionFormatter(sortDirection: SortDirection): string {
    switch (sortDirection) {
      case SortDirection.ASC: return "ascending";
      case SortDirection.DESC: return "descending";
    }
  }

  static visitStatusFormatter: (value: string) => string = v => {
    if (v === "ARRIVED") return "In Port";
    return PortvisitUtils.enumFormatter(v);
  };

  getValueFormatter = (name: string): (value: string) => string => {
    switch (name) {
      case "visitStatus": return VisitOverviewComponent.visitStatusFormatter;
      case "quay": return SentenceCasePipe.format;
      case "orderStatus": case "harbourDues/status": return PortvisitUtils.enumFormatter;
      default: return null;
    }
  }

  getValuesSelectedCallback = (name: string): (facet: FacetStats, selectedValues: string[]) => string[] => {
    switch (name) {
      case "overlandedContainers": case "shortlandedContainers":
        return (facet, selectedValues) => selectedValues.includes("true")
          ? AppContext.isAdmin() ? ["ADMIN"] : [AppContext.userProfile.organisation.shortName]
          : [];
      default: return null;
    }
  }

  protected sortingFormatter = (sortingProperty: VisitSummarySorting): string => {
    switch (sortingProperty.property) {
      case VisitSummarySortingProperty.ARRIVAL_DATE: return `Arrival (${this.getSortDirectionFormatter(sortingProperty.direction)})`;
      case VisitSummarySortingProperty.CREATED: return `Created (${this.getSortDirectionFormatter(sortingProperty.direction)})`;
    }
  }

  filterFacetValues = (name: string, values: FacetValueResult[]): FacetValueResult[] => {
    switch (name) {
      case "overlandedContainers": case "shortlandedContainers":
        const facetValues = values.find(v => AppContext.isAdmin() ? v.value === "ADMIN"
          :  v.value === AppContext.userProfile.organisation.shortName);
        return [{
          value: "true",
          count: facetValues?.count || 0
        }];
      default:
        return values;
    }
  }

  ownCallsFormatter = (ownCallsOnly: boolean) => ownCallsOnly ? "My calls" : "All calls";

  showFeedbackButtonBasedOnLanguage() {
    var userLang = navigator.language || navigator["userLanguage"];
    var feedbackButton = document.getElementById('feedbackButton');
    if (userLang.startsWith("nl")) {
      // Stelt de link en tekst in voor Nederlands sprekende browsers
      feedbackButton["href"] = "https://forms.office.com/e/m36kxNysYv";
      feedbackButton.textContent = 'Geef feedback';
      feedbackButton.style.display = 'block';
    } else {
      // Stelt een andere link en tekst in voor niet-Nederlands sprekende browsers
      feedbackButton["href"] = "https://forms.office.com/e/mDRwUyxE3C";
      feedbackButton.textContent = 'Give feedback';
      feedbackButton.style.display = 'block';
    }
  }

  ranges: MomentDateFieldRange[] = [
    {
      label: 'Today',
      start: moment().startOf('day'),
      end: moment().add(1, "day").startOf('day').subtract(1, 'day')
    },
    {
      label: 'Yesterday',
      start: moment().subtract(1, 'day').startOf('day'),
      end: moment().startOf('day').subtract(1, 'day')
    },
    {
      label: 'This week',
      start: moment().startOf('week'),
      end: moment().add(1, "week").startOf('week').subtract(1, 'day')
    },
    {
      label: 'Last 7 days',
      start: moment().subtract(7, 'day').startOf('day'),
      end: moment().add(1, "day").startOf('day')
    },
    {
      label: 'Next 7 days',
      start: moment().startOf('day'),
      end: moment().add(7, "day").startOf('day').subtract(1, 'day')
    }
  ];

  feedbackEnabled() {
    return false;
  }

  @HandleQuery("getFacetValues", { global: true })
  getFacetValues(facetName: string): Observable<FacetValueResult[]> {
    if (Object.keys(this.filters.overviewFilters.facets).length) {
      return super.getFacetValues(facetName);
    }
    return of(this.allFacets.find(f => f.name === facetName).values);
  }
}

interface VisitOverviewFilters extends FacetedOverviewFilters<VisitOverviewFacetFilters> {
  sortingProperty?: VisitSummarySortingProperty;
  sortDirection?: SortDirection;
  sorting?: VisitSummarySorting;
  ownCallsOnly?: boolean;
  attentionRequired?: boolean;
}

interface VisitOverviewFacetFilters {
  timeRange?: DateFieldRange;
}

interface VisitSummarySorting {
  property: VisitSummarySortingProperty;
  direction: SortDirection;
}
